import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import FilterRulesEditor from './FilterRulesEditor';
import { CODE_FONT } from './formParts';
import { previewSpanVariables } from 'features/evals3/api/evals3Api';

/**
 * Trace tags: a run's spans, or a conversation's runs, dragged into a step as
 * a property and configured on the tag — "the last tool_selection", "every
 * search", "the first run of the thread".
 *
 * Drag Spans (or Runs) from the fields into a judge prompt, a decision step's
 * state or Python code. It lands as a tag that reads all of them, input and
 * output; the tag's pencil opens what it reads: which ones (the app's filter
 * chips), what to read, and which of them, with a count and one example from
 * recent traffic. With no match a tag is empty (0 for a count), never a
 * skipped or failed step. A prompt reads {{spans.NAME}} / {{runs.NAME}};
 * Python reads the same text as t.vars["NAME"]. The step's span_vars are kept
 * in step with the tags in its text: a tag added is a variable, a tag removed
 * is gone. The server's spanVariables.js is the contract.
 */

const PICK_OPTIONS = [
  { value: 'all', label: 'All of them' },
  { value: 'first', label: 'The first' },
  { value: 'last', label: 'The last' },
  { value: 'nth', label: 'The Nth' },
];
// Kept readable for a variable saved before the list was cut down.
const LEGACY_PICKS = [{ value: 'first_n', label: 'The first N' }, { value: 'last_n', label: 'The last N' }];
export const PICKS = [...PICK_OPTIONS, ...LEGACY_PICKS];

// The same choices for spans and runs. Timeline is the trace tree of every match.
const READ_OPTIONS = [
  { value: 'input_output', label: 'Input and output' },
  { value: 'input', label: 'Input' },
  { value: 'output', label: 'Output' },
  { value: 'duration_ms', label: 'Duration' },
  { value: 'tokens', label: 'Tokens' },
  { value: 'timeline', label: 'Timeline' },
];
// Kept readable for a variable saved before the list was cut down.
const LEGACY_READS = [
  { value: 'names', label: 'Names, in order' },
  { value: 'error', label: 'Error' },
  { value: 'metadata', label: 'A metadata key' },
  { value: 'structure', label: 'Outline of the run' },
  { value: 'count', label: 'How many' },
  { value: 'exists', label: 'Whether any' },
];
export const READS = [...READ_OPTIONS, ...LEGACY_READS];
// Reads that are about every match, so "which of them" does not apply.
const READS_ALL = (s) => ['timeline', 'count', 'exists'].includes(s.read);

const NAME_RE = /^[a-z][a-z0-9_]*$/;
const label = (list, v) => (list.find((o) => o.value === v) || list[0]).label;
const unitOf = (spec) => (spec && spec.unit === 'runs' ? 'runs' : 'spans');

/** The tags a step can take: a run's spans; a conversation's runs and spans; nothing on a span. */
export function unitsFor(target) {
  if (target === 'span') return [];
  if (target === 'conversation') return ['runs', 'spans'];
  return ['spans'];
}

/** A new variable's name: spans, spans_2, … (one namespace: Python reads both kinds as t.vars). */
export function freshName(existing, base = 'spans') {
  const taken = new Set(existing);
  if (!taken.has(base)) return base;
  for (let i = 2; ; i += 1) if (!taken.has(`${base}_${i}`)) return `${base}_${i}`;
}

/** The text a tag is in a step: {{spans.NAME}} in a prompt, t.vars["NAME"] in Python. */
export function refFor(unit, name, mode) {
  return mode === 'python' ? `t.vars["${name}"]` : `{{${unit}.${name}}}`;
}

const PROMPT_TAG_RE = /\{\{\s*(spans|runs)\.([a-z][a-z0-9_]*)\s*\}\}/g;
const PY_TAG_RE = /\bt\.vars(?:\[\s*["']([a-z][a-z0-9_]*)["']\s*\]|\.([a-z][a-z0-9_]*))/g;

/** Every tag in the texts: [{ name, unit }] (unit null in Python, where the text does not say). */
export function refsIn(texts, mode) {
  const out = [];
  for (const text of texts) {
    if (typeof text !== 'string') continue;
    if (mode === 'python') for (const m of text.matchAll(PY_TAG_RE)) out.push({ name: m[1] || m[2], unit: null });
    else for (const m of text.matchAll(PROMPT_TAG_RE)) out.push({ name: m[2], unit: m[1] });
  }
  return out;
}

/**
 * The step's variables, in step with the tags in its text: one per tag (a new
 * tag reads all of them, input and output), none for a tag that is gone.
 */
export function syncVars(vars, texts, mode) {
  const refs = refsIn(texts, mode);
  const kept = (vars || []).filter((v) => refs.some((r) => r.name === v.name && (!r.unit || r.unit === unitOf(v))));
  for (const r of refs) {
    if (kept.some((v) => v.name === r.name)) continue;
    const unit = r.unit || (r.name.startsWith('runs') ? 'runs' : 'spans');
    kept.push({ name: r.name, ...(unit === 'runs' ? { unit } : {}), pick: 'all', read: 'input_output' });
  }
  return kept;
}

/** `cfg` with these span_vars; the key is dropped when there are none. */
export function setVars(cfg, vars) {
  const next = { ...cfg };
  if (vars && vars.length) next.span_vars = vars; else delete next.span_vars;
  return next;
}

/** `cfg` with span_vars in step with the texts. */
export function withVars(cfg, texts, mode) {
  return setVars(cfg, syncVars(cfg.span_vars, texts, mode));
}

/** Rename a tag in a text (the popup's name field). */
export function renameRef(text, from, to, unit, mode) {
  if (typeof text !== 'string' || from === to) return text;
  if (mode === 'python') return text.replace(new RegExp(`\\bt\\.vars(\\[\\s*["'])${from}(["']\\s*\\])`, 'g'), `t.vars$1${to}$2`).replace(new RegExp(`\\bt\\.vars\\.${from}\\b`, 'g'), `t.vars.${to}`);
  return text.replace(new RegExp(`\\{\\{\\s*${unit}\\.${from}\\s*\\}\\}`, 'g'), `{{${unit}.${to}}}`);
}

/** One line a person reads: "The last · kind is tool · input and output". */
export function summaryOf(spec) {
  const unit = unitOf(spec);
  const conds = ((spec.where && spec.where.all) || []).map((r) => {
    const field = String(r.path || '').replace(/^(span|run)\./, '');
    const v = Array.isArray(r.value) ? r.value.join(' or ') : r.value;
    return v === undefined ? field : `${field} ${r.op === 'eq' || r.op === 'in' ? 'is' : r.op} ${v}`;
  });
  const which = spec.pick === 'nth' || spec.pick === 'first_n' || spec.pick === 'last_n'
    ? label(PICKS, spec.pick).replace('N', String(spec.n ?? 'N')) : label(PICKS, spec.pick || 'all');
  const parts = [READS_ALL(spec) ? null : which, conds.length ? conds.join(', ') : `every ${unit === 'runs' ? 'run' : 'span'}`];
  if (spec.within) parts.push(spec.within.direct ? 'directly under a match' : 'inside a match');
  parts.push(spec.read === 'metadata' ? `metadata.${spec.metadata_key || '…'}` : label(READS, spec.read || 'input_output').toLowerCase());
  return parts.filter(Boolean).join(' · ');
}

/**
 * A spec as the server expects it: no empty filters, no stray fields. What
 * the popup saves always renders, empty when nothing matched: an older
 * if_none and within are dropped (the popup no longer offers them).
 */
export function cleanSpec(s) {
  const out = { name: s.name, pick: READS_ALL(s) ? 'all' : s.pick || 'all', read: s.read || 'input_output' };
  if (s.unit === 'runs') out.unit = 'runs';
  if (s.where && Array.isArray(s.where.all) && s.where.all.length) out.where = s.where;
  else if (s.where && Array.isArray(s.where.any) && s.where.any.length) out.where = s.where;
  if (['nth', 'first_n', 'last_n'].includes(out.pick)) out.n = Number(s.n) || 1;
  if (out.read === 'metadata') out.metadata_key = s.metadata_key || '';
  for (const k of ['max_items', 'field_chars', 'total_chars']) if (s[k]) out[k] = Number(s[k]);
  return out;
}

/**
 * A step's tags: what the fields rail offers to drag, the text a drag drops,
 * and the popup a tag's pencil opens. `getTexts()` / `setTexts(texts, vars)`
 * read and write the step's text fields (a prompt's system and user, a state,
 * Python source) together with its variables, in one update.
 */
export function useTraceTags({ vars = [], target, mode, getTexts, apply, scopes, teamId, agentName }) {
  const [editing, setEditing] = useState(null);
  const units = unitsFor(target || 'run');
  const tagRef = useCallback((unit) => refFor(unit, freshName((vars || []).map((v) => v.name), unit), mode), [vars, mode]);
  const describeVar = useCallback((name) => {
    const v = (vars || []).find((x) => x.name === name);
    return v ? `${unitOf(v) === 'runs' ? 'Runs' : 'Spans'} · ${summaryOf(v)}` : null;
  }, [vars]);
  const spec = editing ? (vars || []).find((v) => v.name === editing) : null;
  const save = (next) => {
    const from = spec.name;
    const texts = getTexts().map((t) => renameRef(t, from, next.name, unitOf(spec), mode));
    apply(texts, (vars || []).map((v) => (v.name === from ? next : v)));
    setEditing(null);
  };
  const dialog = spec ? (
    <SpanVariableDialog initial={spec} taken={(vars || []).map((v) => v.name).filter((n) => n !== spec.name)}
      scopes={scopes} target={target} teamId={teamId} agentName={agentName} onSave={save} onClose={() => setEditing(null)} />
  ) : null;
  return { units, tagRef, edit: setEditing, describeVar, dialog };
}

const TAG_LABELS = { spans: 'Spans', runs: 'Runs' };

/**
 * The rail's first group: the tags, as rows a person drags into the step (or
 * clicks to insert at the caret). Each drop is a new tag with a fresh name.
 */
export function tagGroup(units, tagRef) {
  if (!units.length) return null;
  return {
    id: 'tags',
    label: 'Pick from the trace',
    fields: units.map((u) => ({ path: u, label: TAG_LABELS[u], type: 'pick', common: true, python: tagRef(u), prompt: tagRef(u) })),
  };
}

/** Trace rows the tags replace, so the rail offers one way to read them (the transcript stays: it is text, not a list). */
export function hiddenByTags(target) {
  if (target === 'span') return [];
  if (target === 'conversation') return ['conversation.runs'];
  return ['spans'];
}

function Row({ label: text, children }) {
  return (
    <div className="mb-3">
      <div className="mb-1 text-[12px] text-[color:var(--text-muted)]">{text}</div>
      {children}
    </div>
  );
}

function Preview({ result, error, loading, spec }) {
  const of = unitOf(spec) === 'runs' ? 'conversations' : 'runs';
  const one = of === 'runs' ? 'run' : 'conversation';
  const muted = 'text-[12px] text-[color:var(--text-muted)]';
  if (error) return <div className="text-[12px] text-danger">{error}</div>;
  if (!result) return <div className={muted}>{loading ? `Reading the last 50 ${of}…` : ''}</div>;
  if (!result.sampled) return <div className={muted}>No {of} yet to try it on.</div>;
  const v = result.variables[0];
  const ex = v.example;
  // A new choice keeps the last result on screen, dimmed, until its own arrives.
  return (
    <div className={`text-[12px] transition-opacity ${loading ? 'opacity-50' : ''}`} aria-busy={loading}>
      <div className="mb-2 text-[color:var(--text-primary)]">
        Matched in <span className="tabular-nums font-medium">{v.matched_units}</span> of the last <span className="tabular-nums">{result.sampled}</span> {of}
      </div>
      {ex && (
        <>
          <div className="mb-1 text-[color:var(--text-muted)]">{ex.matched ? `Latest ${one} it matched` : `Latest ${one}: nothing matched, so the tag is empty`}</div>
          <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap break-words rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2.5 text-[12px] leading-relaxed text-[color:var(--text-primary)]" style={{ fontFamily: CODE_FONT }}>
            {ex.text || '(empty)'}
          </pre>
        </>
      )}
    </div>
  );
}

/** The popup a tag's pencil opens: which ones, what to read, which of them. */
export function SpanVariableDialog({ initial, taken = [], scopes, target, teamId, agentName, onSave, onClose }) {
  const [spec, setSpec] = useState(() => ({ ...initial }));
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (patch) => setSpec((s) => ({ ...s, ...patch }));
  const unit = unitOf(spec);
  const level = unit === 'runs' ? 'run' : 'span';
  const ownScopes = useMemo(() => ({ [level]: (scopes && scopes[level]) || [] }), [scopes, level]);
  const reads = [...READ_OPTIONS, ...LEGACY_READS.filter((r) => r.value === initial.read)];
  const picks = [...PICK_OPTIONS, ...LEGACY_PICKS.filter((p) => p.value === spec.pick)];

  const nameProblem = !NAME_RE.test(spec.name || '') ? 'lower_snake_case, starting with a letter'
    : taken.includes(spec.name) ? 'another tag has this name' : null;
  const clean = useMemo(() => cleanSpec(spec), [spec]);
  const key = JSON.stringify(clean);

  useEffect(() => {
    // On window, after the pickers' document listeners: an Escape a picker
    // inside already handled closes only that picker.
    const onKey = (e) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!teamId || nameProblem) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      previewSpanVariables(teamId, { target: target === 'conversation' ? 'conversation' : 'run', agent_name: agentName || undefined, span_vars: [clean], sample: 50 })
        .then((r) => { if (!cancelled) { setResult(r); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e?.response?.data?.message || e?.response?.data?.error || e.message); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, teamId, agentName, target]);

  const needsN = ['nth', 'first_n', 'last_n'].includes(spec.pick);
  const canSave = !nameProblem && !(spec.read === 'metadata' && !String(spec.metadata_key || '').trim());
  const noun = unit === 'runs' ? 'runs' : 'spans';

  const el = (
    <div className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[6vh]"
      style={{ background: 'var(--surface-overlay)' }}
      role="dialog" aria-modal="true" aria-labelledby="span-var-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[86vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-2xl">
        <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-5 py-3">
          <h2 id="span-var-title" className="flex items-center gap-2 text-[14px] font-semibold text-[color:var(--text-primary)]">
            {unit === 'runs' ? 'Runs' : 'Spans'}
            <input aria-label="Tag name" value={spec.name} onChange={(e) => set({ name: e.target.value.trim() })}
              className="input h-8 !w-44 font-mono text-[12px] font-normal" />
          </h2>
          <button type="button" aria-label="Close" onClick={onClose} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X size={16} aria-hidden /></button>
        </div>
        {nameProblem && <div className="px-5 pt-2 text-[11px] text-danger">{nameProblem}</div>}
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-auto px-5 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div>
            <Row label={`Which ${noun}`}>
              <FilterRulesEditor value={spec.where || { all: [] }} target={level} scopes={ownScopes} teamId={teamId}
                onChange={(where) => set({ where })} />
            </Row>
            <Row label="Read">
              <BrandDropdown ariaLabel="Read" value={spec.read || 'input_output'} onChange={(read) => set({ read })} options={reads} />
              {spec.read === 'metadata' && (
                <input aria-label="Metadata key" placeholder="key" value={spec.metadata_key || ''} onChange={(e) => set({ metadata_key: e.target.value })} className="input mt-1.5 h-8 w-full font-mono text-[12px]" />
              )}
            </Row>
            {!READS_ALL(spec) && (
              <Row label="Which of them">
                <div className="flex items-center gap-1.5">
                  <div className="min-w-0 flex-1"><BrandDropdown ariaLabel="Which of them" value={spec.pick || 'all'} onChange={(pick) => set({ pick })} options={picks} /></div>
                  {needsN && (
                    <input aria-label="N" type="number" value={spec.n ?? 1} onChange={(e) => set({ n: e.target.value === '' ? '' : Number(e.target.value) })}
                      className="input h-8 w-16 tabular-nums" title={spec.pick === 'nth' ? 'from the start; negative counts from the end' : undefined} />
                  )}
                </div>
              </Row>
            )}
          </div>
          <div className="min-w-0">
            <Preview result={result} error={error} loading={loading} spec={clean} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[color:var(--border-default)] px-5 py-3">
          <button type="button" onClick={onClose} className="h-8 rounded-md px-3 text-[12.5px] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]">Cancel</button>
          <button type="button" disabled={!canSave} onClick={() => onSave(clean)} className="h-8 rounded-md bg-brand px-3 text-[12.5px] font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-50">Save</button>
        </div>
      </div>
    </div>
  );
  return createPortal(el, document.body);
}
